import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IRoutingConfig } from '../../../models/models-index';
import { AuthenticationService, RoleConfigService, RuntimeConfigService } from '../../services-index';
import * as Models from '../../../models/models-index';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../_store/app-state.model';
import { selectLoggedInUser } from '../../../../_store/app.selectors';

@Injectable()
export class RoutingConfigService {
    routeUrls: string[] = [];
    userRole: string;

    // Routes available to the user in the nav (includes both dynamic and static)
    get userNavRouteConfigs(): Models.IRouteConfig[] {
        const allRoutes = this.runtimeConfigService.routingConfig;
        // console.log('RoutingConfigService: Current user role:', this.userRole);
        // console.log('RoutingConfigService: All available routes:', allRoutes.map(r => ({ category: r.category, roles: r.roles, isStatic: r.isStatic })));

        // Filter routes by user role and navigation display preference
        const accessibleRoutes = allRoutes
            .filter(route => {
                // Check role access
                if (route.roles?.length && !route.roles.includes(this.userRole)) {
                    // console.log(`RoutingConfigService: Filtering out ${route.category} - user role ${this.userRole} not in required roles:`, route.roles);
                    return false;
                }
                // Only include routes marked for navigation display
                return route.displayInNav === true;
            })
            // remove children that don't have role access
            .map(route => {
              let newRoute = { ...route, children: route?.children?.filter(child => child?.roles?.length > 0 ? child.roles.includes(this.userRole) : true) }
              // console.log("RoutingConfigService: ChildFilteredRoute:", newRoute)
              return newRoute
            })
            .sort((a, b) => {
                // Sort by order property first, then by displayName as fallback
                if (a.order !== b.order) {
                    return a.order - b.order;
                }
                return a.displayName.localeCompare(b.displayName);
            });

        // console.log('RoutingConfigService: Accessible nav routes:', accessibleRoutes.map(r => r.category));
        return accessibleRoutes;
    }
    // All routes available to the user
    get allUserRouteConfigs(): Models.IRouteConfig[] {
        const allRoutes = this.runtimeConfigService.routingConfig;

        // Filter routes by user role (but don't filter by displayInNav for this getter)
        return allRoutes.filter(route => {
            if (route.roles?.length && !route.roles.includes(this.userRole)) {
                return false;
            }
            return true;
        });
    }
    // Default route for the user
    get defaultRoute(): string {
        return this.getDefaultRoute();
    }

    // Paths for all routes available to the user including those not in the nav
    get userRouteUrls(): string[] {
        if (this.routeUrls.length) {
            return this.routeUrls;
        }
        let routeUrls: string[] = [];
        // console.log('RoutingConfigService: Generating userRouteUrls for role:', this.userRole);

        const getChildRoutes = (parentPath: string, children: Models.IRouteDetails[]) => {
          children.forEach(child => {
                if (child.roles?.length && !child.roles.includes(this.userRole)) {
                    return;
                }
                const childPath = `${parentPath}/${child.route}`;

                routeUrls.push(childPath);

                // If the child has its own children, recursively get their routes
                if ((child?.children || []).length > 0) {
                    getChildRoutes(childPath, child.children);
                }
            });
        };

        // Use the unified routingConfig from runtimeConfigService
        this.runtimeConfigService.routingConfig.forEach(route => {
            if (route.roles?.length && !route.roles.includes(this.userRole)) {
                return;
            }

            if (route.isStatic) {
                // For static routes, extract all routes from Angular router configuration
                const staticRoutes = this.getStaticRouteUrls(route.category);
                routeUrls.push(...staticRoutes);
            } else {
                // For dynamic routes, use the API config
                const parent = `/${route.category}`; // Use category instead of id to match URL structure
                
                if ((route.children || []).length === 0) {
                    routeUrls.push(parent);
                } else {
                    getChildRoutes(parent, route.children);
                }
            }
        });

        console.log('RoutingConfigService: Generated userRouteUrls:', routeUrls);
        this.routeUrls = routeUrls;
        return routeUrls;
    }

    /**
     * Extract all route URLs from Angular router configuration for a static module
     */
    private getStaticRouteUrls(category: string): string[] {
        const staticUrls: string[] = [];
        
        // Find the static route in the Angular router config
        const staticRoute = this.router.config.find(route => route.path === category);
        if (!staticRoute || !staticRoute.loadChildren) {
            console.warn(`Static route ${category} not found in Angular router config`);
            return staticUrls;
        }

        // Try to extract routes from the current router configuration
        // This will work if the module has already been loaded
        this.extractRoutesFromRouter(category, staticUrls);
        
        return staticUrls;
    }

    /**
     * Extract actual routes from the Angular router for a given category
     */
    private extractRoutesFromRouter(category: string, urls: string[]): void {
        const baseUrl = `/${category}`;
        urls.push(baseUrl);

        // Get all routes from the router that start with our category
        const allRoutes = this.getAllRoutesFromRouter();
        const categoryRoutes = allRoutes.filter(route => route.startsWith(baseUrl));
        
        if (categoryRoutes.length > 0) {
            // If we found routes in the router, use those
            urls.push(...categoryRoutes);
            console.log(`RoutingConfigService: Found ${categoryRoutes.length} routes for ${category} from Angular router`);
        } else {
            // Fallback: generate some basic patterns if module hasn't been loaded yet
            console.log(`RoutingConfigService: No routes found for ${category} in router, using fallback patterns`);
            this.generateFallbackRoutes(category, urls);
        }
    }

    /**
     * Recursively extract all route paths from the Angular router configuration
     */
    private getAllRoutesFromRouter(): string[] {
        const allPaths: string[] = [];

        const extractPaths = (routes: any[], parentPath: string = '') => {
            routes.forEach(route => {
                if (route.path !== undefined && route.path !== '') {
                    const fullPath = parentPath ? `${parentPath}/${route.path}` : `/${route.path}`;

                  // Remove route parameters (like :id) for authorization comparison
                    const pathWithoutParams = this.stripRouteParameters(fullPath);
                    if (pathWithoutParams && !allPaths.includes(pathWithoutParams)) {
                        allPaths.push(pathWithoutParams);
                    }
                }

                // Recursively process child routes
                if (route.children && route.children.length > 0) {
                    const currentPath = parentPath ? `${parentPath}/${route.path || ''}` : `/${route.path || ''}`;
                    extractPaths(route.children, currentPath);
                }

                // Process lazy-loaded routes if they've been loaded
                if (route._loadedRoutes) {
                    const currentPath = parentPath ? `${parentPath}/${route.path || ''}` : `/${route.path || ''}`;
                    extractPaths(route._loadedRoutes, currentPath);
                }
            });
        };

        extractPaths(this.router.config);
        return allPaths;
    }

    /**
     * Strip route parameters from a path for authorization comparison
     */
    private stripRouteParameters(path: string): string {
        // Remove parameters like :id, :queryConfigId, etc.
        return path.replace(/\/:[^\/]+/g, '');
    }

    /**
     * Fallback route generation for when the module hasn't been loaded yet
     */
    private generateFallbackRoutes(category: string, urls: string[]): void {
        const baseUrl = `/${category}`;
        
        // Just add the base route - the module will handle its own authorization
        // once it's loaded, or the AuthGuard can be more permissive for static routes
        urls.push(baseUrl);
        
        // We could add some very basic patterns here if needed, but the goal
        // is to rely on the static route check in AuthGuard being permissive
        console.log(`RoutingConfigService: Using minimal fallback for ${category}`);
    }

    constructor(private readonly roleConfigService: RoleConfigService,
        private readonly authenticationService: AuthenticationService,
        private readonly runtimeConfigService: RuntimeConfigService,
        private readonly router: Router,
        private readonly store$: Store<AppState>) {
        this.store$.select(selectLoggedInUser).subscribe(user => {
            this.userRole = user?.role;
            // reset routeUrls so they can be recomputed again
            this.routeUrls = [];
        });
    }

    private getDefaultRoute(): string {
        if (!this.userNavRouteConfigs?.length) {
            return '';
        }

        const route = this.userNavRouteConfigs.find(r => r.default) ?? this.userNavRouteConfigs[0];

        // For static routes, use the defaultRoute property directly
        if (route.isStatic && route.defaultRoute) {
            return route.defaultRoute;
        }

        // For dynamic routes, construct from children
        const child = route.children?.find(r => r.default) ?? route.children?.[0];
        const path = child?.route ?? '';

        return `/${route.id}/${path}`;
    }

    computeRoutes(forNavBar: boolean = true): Models.IRouteConfig[] {
        const user = this.authenticationService.getUser();

        return !user
            ? []
            : this.runtimeConfigService.routingConfig
                .filter(route => (route.displayInNav && this.userHasAccessToRoute(user.role, route)) || !forNavBar)
                .map(route => {

                    // Recursively filter children based on user access
                    const filteredChildren = this.filterChildrenRecursively(route.children, user.role);

                    // For static routes, use the predefined defaultRoute
                    let defaultRoute: string;
                    if (route.isStatic && route.defaultRoute) {
                        defaultRoute = route.defaultRoute;
                    } else {
                        // For dynamic routes, construct from children
                        const child = filteredChildren.find(r => r.default) ?? route.children?.[0];
                        const path = child?.route ?? '';
                        defaultRoute = `/${route.id}/${path}`;
                    }

                    // construct the route and set the children & defaultRoutes according to the user's role
                    let routeConfig: Models.IRouteConfig = {
                        ...route,
                        route: route.route ?? '',
                        children: filteredChildren,
                        defaultRoute: defaultRoute
                    };
                    return routeConfig;
                });

    }

    private filterChildrenRecursively(
        children: Models.IRouteDetails[],
        userRole: string
    ): Models.IRouteDetails[] {
        if (!children || children.length === 0) {
            return [];
        }

        return children
            .filter(child => this.userHasAccessToChildRoute(userRole, child) && child.displayInNav !== false)
            .map(child => ({
                ...child,
                children: this.filterChildrenRecursively(child.children, userRole) // Recursively filter children of children
            }));
    }

    public userHasAccessToRoute(userRole: string, routeData: Models.IRouteConfig): boolean {
        const routeRoles = routeData.roles || [];

        if (!!routeData.disableForDealerGroups && this.roleConfigService.isDealerGroupRole(userRole)) {
            return false;
        }
        else {
            return (routeRoles).length === 0 ? true : routeRoles.indexOf(userRole) != -1;
        }
    }

    public userHasAccessToChildRoute(userRole: string, routeData: Models.IRouteDetails): boolean {
        const routeRoles = routeData.roles || [];

        return routeRoles.length === 0 ? true : routeRoles.indexOf(userRole) != -1;
    }
}
